import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert
} from "@mui/material";
import { 
  applyActionCode, 
  checkActionCode, 
  confirmPasswordReset, 
  verifyPasswordResetCode 
} from "firebase/auth";
import { useFirebase } from "./contexts/FirebaseContext";
import { useAuth } from "./contexts/AuthContext";

function AuthAction() {
  // Shared state
  const [verifying, setVerifying] = useState(true);
  const [actionType, setActionType] = useState(null);
  const [actionCode, setActionCode] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Password reset specific state
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useFirebase();
  const { updateUserProfile } = useAuth();

  useEffect(() => {
    const verifyActionCode = async () => {
      try {
        // Get the action code from the URL
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get("oobCode");
        const mode = queryParams.get("mode");

        if (!code) {
          throw new Error("No action code found in URL");
        }

        setActionCode(code);

        // Check the action code to determine the type
        const actionInfo = await checkActionCode(auth, code);
        
        // Set the action type based on the mode or the action info
        if (mode === "verifyEmail" || actionInfo.operation === "VERIFY_EMAIL") {
          setActionType("verifyEmail");
        } else if (mode === "resetPassword" || actionInfo.operation === "PASSWORD_RESET") {
          setActionType("resetPassword");
          // For password reset, we need to verify the code to get the email
          const emailForReset = await verifyPasswordResetCode(auth, code);
          setEmail(emailForReset);
        } else {
          throw new Error("Unsupported action type");
        }
        
        setVerifying(false);
      } catch (error) {
        console.error("Error verifying action code:", error);
        setError(error.message || "The link may be invalid or expired.");
        setVerifying(false);
      }
    };

    verifyActionCode();
  }, [location, auth]);

  // Email verification handler
  const handleVerifyEmail = async () => {
    try {
      setLoading(true);
      await applyActionCode(auth, actionCode);
      
      // Update user profile if needed
      await updateUserProfile({ 
        emailVerified: true
      });
      
      setSuccess(true);
    } catch (error) {
      console.error("Error verifying email:", error);
      setError(error.message || "Failed to verify email. The link may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  // Password reset validation
  const validatePasswordForm = () => {
    // Reset error
    setError(null);

    // Check if all fields are filled
    if (!newPassword || !confirmPassword) {
      setError("All fields are required");
      return false;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Check password strength (at least 6 characters)
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    return true;
  };

  // Password reset handler
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Confirm password reset
      await confirmPasswordReset(auth, actionCode, newPassword);
      
      setSuccess(true);
    } catch (error) {
      console.error("Error resetting password:", error);
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case "auth/expired-action-code":
          setError("The password reset link has expired");
          break;
        case "auth/invalid-action-code":
          setError("The password reset link is invalid");
          break;
        case "auth/weak-password":
          setError("Password is too weak. Please choose a stronger password");
          break;
        default:
          setError("Failed to reset password. Please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToHome = () => {
    navigate("/");
  };

  const handleGoToLogin = () => {
    navigate("/signin");
  };

  const handleGoToSettings = () => {
    navigate("/settings");
  };

  // Render based on action type and state
  const renderContent = () => {
    if (verifying) {
      return (
        <>
          <CircularProgress size={60} sx={{ my: 4 }} />
          <Typography variant="body1">
            Verifying your link...
          </Typography>
        </>
      );
    }

    if (error && !actionType) {
      return (
        <>
          <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
            Invalid Link
          </Alert>
          <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            {error || "There was a problem with your link. It may be invalid or expired."}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleGoToHome}>
              Go to Home
            </Button>
          </Box>
        </>
      );
    }

    // Email verification UI
    if (actionType === "verifyEmail") {
      if (success) {
        return (
          <>
            <Alert severity="success" sx={{ width: "100%", mb: 3 }}>
              Your email has been successfully verified!
            </Alert>
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              Thank you for verifying your email address. You can now access all features of your account.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={handleGoToHome}>
                 Go to Home
               </Button>
               <Button variant="contained" onClick={handleGoToSettings}>
                 Go to Settings
              </Button>
            </Box>
          </>
        );
      }

      return (
        <>
          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 3 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            Please click the button below to verify your email address.
          </Typography>
          <Button 
            variant="contained" 
            onClick={handleVerifyEmail}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Verify Email"}
          </Button>
        </>
      );
    }

    // Password reset UI
    if (actionType === "resetPassword") {
      if (success) {
        return (
          <>
            <Alert severity="success" sx={{ width: "100%", mb: 3 }}>
              Password Reset Successful!
            </Alert>
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              Your password has been successfully reset. You can now log in with your new password.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleGoToLogin}>
                Go to Login
              </Button>
            </Box>
          </>
        );
      }

      return (
        <Box component="form" onSubmit={handleResetPassword} sx={{ width: "100%" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Create a new password for <strong>{email}</strong>
          </Typography>
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label="New Password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Reset Password"}
          </Button>
        </Box>
      );
    }

    return (
      <Typography variant="body1">
        Unsupported action type.
      </Typography>
    );
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          {actionType === "verifyEmail" ? "Email Verification" : 
           actionType === "resetPassword" ? "Reset Password" : 
           "Authentication Action"}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {renderContent()}
        </Box>
      </Paper>
    </Container>
  );
}

export default AuthAction; 